/* eslint-disable @typescript-eslint/no-explicit-any */
// PaymentForm.tsx - FIXED VERSION with improved M-Pesa validation
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  usePaymentContext,
  type PaymentMethod,
} from "../context/PaymentContext";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import mpesaService from "../service/mpesaService";

// API Base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE;

// Enhanced Stripe promise with better error handling and retry logic
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "pk_test_dummy_key",
  {
    apiVersion: "2023-10-16",
    locale: "en",
  },
).catch((error) => {
  console.error("Failed to load Stripe:", error);
  return null;
});

// Enhanced fetch with retry logic
const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  retries = 3,
): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    if (!response.ok && retries > 0) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response;
  } catch (error) {
    if (retries > 0) {
      console.log(`Retrying request... ${retries} attempts left`);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return fetchWithRetry(url, options, retries - 1);
    }
    throw error;
  }
};

// Loading Spinner Component
const LoadingSpinner: React.FC<{ message?: string; isDark: boolean }> = ({
  message = "Processing your payment...",
  isDark,
}) => (
  <div className="flex flex-col items-center justify-center py-8">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
      <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
    </div>
    <p
      className={`mt-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}
    >
      {message}
    </p>
  </div>
);

// Custom hook for payment processing
const usePaymentProcessor = () => {
  const { state, setLoading, setError, setPaymentIntent } = usePaymentContext();
  const {
    updateUser,
    setJobPostingSubscription,
    setWhitelistSubscription,
    updateWhitelistStatus,
  } = useAuth();

  const processPayment = useCallback(
    async (
      stripe: any,
      cardElement: any,
      onSuccess: (paymentIntent: any) => void,
    ): Promise<void> => {
      if (!state.selectedPlan) {
        setError("No plan selected");
        return;
      }

      setLoading(true);
      setError("");

      // ADD DEBUG LOG HERE
      console.log("🔍 PaymentProcessor State:", {
        mpesaPhoneNumber: state.mpesaPhoneNumber,
        paymentMethod: state.paymentMethod,
        selectedPlan: state.selectedPlan?.name,
        isLoading: state.isLoading, // Check the actual state property
      });

      try {
        const amount =
          state.billingPeriod === "monthly"
            ? state.selectedPlan.monthlyPrice
            : state.selectedPlan.annualPrice;

        switch (state.paymentMethod) {
          case "mpesa":
            await processMpesaPayment(amount, onSuccess);
            break;
          case "card":
            await processStripePayment(stripe, cardElement, amount, onSuccess);
            break;
          case "paypal":
            await processPayPalPayment(amount, onSuccess);
            break;
          default:
            throw new Error("Unsupported payment method");
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Payment failed. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      state.selectedPlan,
      state.billingPeriod,
      state.paymentMethod,
      state.mpesaPhoneNumber,
      state.isLoading,
      setLoading,
      setError,
    ],
  );

  const processMpesaPayment = async (
  amount: number,
  onSuccess: (paymentIntent: any) => void,
): Promise<void> => {
  try {
    console.log("🔍 Starting M-Pesa payment process...");
    
    // 1. Validate phone number
    if (!state.mpesaPhoneNumber?.trim()) {
      throw new Error("Please enter your M-Pesa phone number");
    }

    if (!mpesaService.validatePhoneNumber(state.mpesaPhoneNumber)) {
      throw new Error(
        "Please enter a valid Kenyan phone number (07XXXXXXXX or 2547XXXXXXXX)"
      );
    }

    const formattedPhone = mpesaService.formatPhoneNumber(state.mpesaPhoneNumber);
    console.log("✅ Formatted phone number:", formattedPhone);

    // 2. Initiate payment
    setLoading(true);
    
    const response = await mpesaService.initiatePayment({
      amount,
      phoneNumber: formattedPhone,
      packageId: state.selectedPlan?.id,
      plan: state.selectedPlan?.name,
      currency: "KES",
      isAnnual: state.billingPeriod === "annual",
    });

    console.log("📨 M-Pesa initiation response:", response);

    if (!response.success) {
      throw new Error(getUserFriendlyMessage(response.error || response.message));
    }

    const { paymentId, checkoutRequestID } = response.data || {};
    
    if (!paymentId || !checkoutRequestID) {
      throw new Error("No payment ID received from M-Pesa service");
    }

    // 3. Poll for payment status
    console.log("⏳ Waiting for payment confirmation...");
    const pollResult = await mpesaService.pollPaymentStatus(
      paymentId,
      checkoutRequestID,
      10,      // max attempts
      30000    // 30 secs
    );

    console.log("M-Pesa payment poll result:", pollResult);

    // 4. Handle final status
    const paymentStatus = pollResult.payment?.status;
    
    if (paymentStatus === "successful") {
      // ✅ Payment successful
      const paymentIntent = {
        id: pollResult.payment.id,
        status: "succeeded",
        amount: pollResult.payment.amount,
        currency: pollResult.payment.currency || "KES",
        created: Math.floor(Date.now() / 1000),
        payment_method_types: ["mobile_money"],
        metadata: {
          mpesaReceiptNumber: pollResult.payment.metadata?.mpesaReceiptNumber,
          paymentId: pollResult.payment.id,
        },
      };

      setPaymentIntent(paymentIntent);
      
      if (pollResult.payment.packageId) {
        await updateUserSubscriptionState(pollResult.payment);
      }
      
      onSuccess(paymentIntent);
      
    } else if (paymentStatus === "failed" || paymentStatus === "cancelled") {
      // ❌ Payment failed
      const failureReason = pollResult.payment?.metadata?.failureReason || 
                           "Payment was not completed";
      throw new Error(getUserFriendlyMessage(failureReason));
      
    } else {
      // ⏳ Payment is still processing (timeout)
      // Don't fail - let user check later
      const processingIntent = {
        id: `mpesa_${Date.now()}`,
        status: "processing",
        amount: amount,
        currency: "KES",
        created: Math.floor(Date.now() / 1000),
        payment_method_types: ["mobile_money"],
        metadata: {
          paymentId,
          checkoutRequestID,
          note: "Payment is being processed. Please check your M-Pesa messages for confirmation.",
        },
      };

      setPaymentIntent(processingIntent);
      
      // Show success message but with note to check status
      console.warn("⚠️ Payment confirmation timeout - check M-Pesa messages");
      onSuccess(processingIntent);
    }
    
  } catch (err) {
    console.error("💥 M-Pesa payment error:", err);
    
    const errorMessage = err instanceof Error 
      ? getUserFriendlyMessage(err.message)
      : "M-Pesa payment failed. Please try again.";
    
    throw new Error(errorMessage);
    
  } finally {
    setLoading(false);
  }
};

// Helper function for user-friendly messages
const getUserFriendlyMessage = (errorMessage: string): string => {
  const error = errorMessage.toLowerCase();
  
  if (error.includes("429") || error.includes("rate limit") || error.includes("too many")) {
    return "Too many attempts. Please wait a minute and try again.";
  }
  if (error.includes("insufficient") || error.includes("balance")) {
    return "Insufficient M-Pesa balance. Please top up and try again.";
  }
  if (error.includes("cancelled") || error.includes("user cancelled")) {
    return "Payment was cancelled. Please try again when ready.";
  }
  if (error.includes("timeout") || error.includes("timed out")) {
    return "Payment confirmation timed out. Please check your M-Pesa messages.";
  }
  if (error.includes("network") || error.includes("connection")) {
    return "Network issue. Please check your connection and try again.";
  }
  if (error.includes("declined") || error.includes("rejected")) {
    return "Payment was declined. Please check your M-Pesa balance and try again.";
  }
  
  // Return original message for unknown errors
  return errorMessage;
};
  const processStripePayment = async (
    stripe: any,
    cardElement: any,
    amount: number,
    onSuccess: (paymentIntent: any) => void,
  ): Promise<void> => {
    if (!stripe || !cardElement) {
      throw new Error("Payment system not ready. Please try again.");
    }

    try {
      // Enhanced element validation
      if (typeof cardElement.mount !== "function") {
        throw new Error(
          "Card input is not properly initialized. Please refresh the page.",
        );
      }

      console.log("Creating payment intent for amount:", amount);

      const intentResponse = await fetchWithRetry(
        `${API_BASE_URL}/payments/methods/stripe/create-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            amount: amount,
            currency: "usd",
            packageId: state.selectedPlan?.id,
            isAnnual: state.billingPeriod === "annual",
          }),
        },
      );

      if (!intentResponse.ok) {
        const errorText = await intentResponse.text();
        let errorMessage = "Failed to create payment intent";

        try {
          if (errorText) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          }
        } catch {
          errorMessage = `Server error: ${intentResponse.status}`;
        }
        throw new Error(errorMessage);
      }

      const responseText = await intentResponse.text();
      if (!responseText) {
        throw new Error("Empty response from server");
      }

      const intentData = JSON.parse(responseText);
      const { clientSecret, paymentId } = intentData;

      if (!clientSecret) {
        throw new Error("No client secret received from server");
      }

      console.log("Confirming payment with Stripe...");

      // Add timeout for confirmation
      const confirmationPromise = stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: state.user?.name || "Customer",
            email: state.user?.email,
          },
        },
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Payment confirmation timeout")),
          30000,
        ),
      );

      const { error: stripeError, paymentIntent } = await Promise.race([
        confirmationPromise,
        timeoutPromise,
      ]);

      if (stripeError) {
        console.error("Stripe confirmation error:", stripeError);

        // Provide more user-friendly error messages
        let userMessage = stripeError.message;
        if (stripeError.code === "card_declined") {
          userMessage = "Your card was declined. Please try a different card.";
        } else if (stripeError.code === "expired_card") {
          userMessage = "Your card has expired. Please use a different card.";
        } else if (stripeError.code === "incorrect_cvc") {
          userMessage =
            "The CVC code is incorrect. Please check and try again.";
        } else if (stripeError.code === "incomplete_number") {
          userMessage =
            "Your card number is incomplete. Please check and try again.";
        } else if (stripeError.code === "incomplete_expiry") {
          userMessage =
            "Your card expiry date is incomplete. Please check and try again.";
        } else if (stripeError.code === "incomplete_cvc") {
          userMessage =
            "Your CVC code is incomplete. Please check and try again.";
        }

        throw new Error(userMessage);
      }

      if (!paymentIntent) {
        throw new Error("No payment intent received from Stripe");
      }

      console.log("Payment intent confirmed:", paymentIntent);

      const confirmResponse = await fetchWithRetry(
        `${API_BASE_URL}/payments/methods/stripe/confirm-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            paymentId: paymentId,
          }),
        },
      );

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json();
        throw new Error(errorData.error || "Payment confirmation failed");
      }

      const result = await confirmResponse.json();
      console.log("Backend confirmation result:", result);

      const successIntent = {
        id: paymentIntent.id,
        status: result.payment?.status || paymentIntent.status,
        amount: result.payment?.amount || amount,
        currency: result.payment?.currency || paymentIntent.currency || "usd",
        created: paymentIntent.created || Math.floor(Date.now() / 1000),
        payment_method_types: paymentIntent.payment_method_types || ["card"],
        metadata: {
          paymentId: result.payment?.id,
          subscriptionId: result.subscription?.id,
          backendStatus: result.payment?.status,
        },
      };

      if (result.payment) {
        setPaymentIntent(successIntent);

        if (result.subscription) {
          await updateUserSubscriptionState(result.subscription);
        }
      }

      onSuccess(successIntent);
    } catch (err) {
      console.error("Stripe payment error:", err);
      throw err;
    }
  };

  const processPayPalPayment = async (
    amount: number,
    onSuccess: (paymentIntent: any) => void,
  ): Promise<void> => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const mockPaymentIntent = {
        id: `paypal_${Date.now()}`,
        status: "succeeded",
        amount,
        currency: "usd",
        created: Math.floor(Date.now() / 1000),
        payment_method_types: ["paypal"],
      };

      setPaymentIntent(mockPaymentIntent);

      const isWhitelistPlan = state.selectedPlan?.name
        ?.toLowerCase()
        .includes("whitelist");

      const mockSubscription = {
        id: `sub_${isWhitelistPlan ? "whitelist" : "job"}_${Date.now()}`,
        status: "active",
        planId: state.selectedPlan?.id
          ? parseInt(state.selectedPlan.id.toString())
          : 0,
        planName: state.selectedPlan?.name,
        planType: isWhitelistPlan ? "whitelist" : "job_posting",
        package: state.selectedPlan?.name || "",
        planCode: state.selectedPlan?.id?.toString() || "",
        expiresAt: calculateExpiryDate(),
        endDate: calculateExpiryDate(),
        billingPeriod: state.billingPeriod,
        createdAt: new Date().toISOString(),
      };

      await updateUserSubscriptionState(mockSubscription);
      onSuccess(mockPaymentIntent);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "PayPal payment failed",
      );
    }
  };

  const updateUserSubscriptionState = async (
    subscription: any,
  ): Promise<void> => {
    try {
      const planId = state.selectedPlan?.id
        ? parseInt(state.selectedPlan.id.toString())
        : 0;

      const isWhitelistPlan = Boolean(
        state.selectedPlan?.name?.toLowerCase().includes("whitelist") ||
          state.selectedPlan?.id?.toString().includes("whitelist"),
      );

      console.log("🔍 Subscription Type Detection:", {
        planName: state.selectedPlan?.name,
        planId: state.selectedPlan?.id,
        isWhitelistPlan,
        subscriptionStatus: subscription.status,
      });

      const subscriptionData = {
        id: subscription.id,
        status: subscription.status,
        planId: planId,
        planName: state.selectedPlan?.name || "",
        planType: isWhitelistPlan ? "whitelist" : "job_posting",
        package: state.selectedPlan?.name || "",
        planCode: state.selectedPlan?.id?.toString() || "",
        isActive:
          subscription.status === "active" ||
          subscription.status === "succeeded",
        expiresAt: subscription.expiresAt || calculateExpiryDate(),
        endDate: subscription.expiresAt || calculateExpiryDate(),
        billingPeriod: state.billingPeriod,
        createdAt: subscription.createdAt || new Date().toISOString(),
      };

      if (isWhitelistPlan) {
        console.log("🎯 Setting whitelist subscription:", subscriptionData);
        setWhitelistSubscription(subscriptionData as any);
        updateWhitelistStatus(true);
      } else {
        console.log("🎯 Setting job posting subscription:", subscriptionData);
        setJobPostingSubscription(subscriptionData as any);
      }

      updateUser({ subscribed: true });
      updateLocalStorageWithSubscription(subscriptionData, isWhitelistPlan);
      dispatchSubscriptionUpdateEvent(subscriptionData);
      await refreshUserData();

      console.log(
        `✅ ${isWhitelistPlan ? "Whitelist" : "Job Posting"} subscription updated:`,
        subscriptionData,
      );
    } catch (error) {
      console.error("Failed to update subscription state:", error);
      throw error;
    }
  };

  const calculateExpiryDate = (): string => {
    const now = new Date();
    if (state.billingPeriod === "annual") {
      return new Date(now.setFullYear(now.getFullYear() + 1)).toISOString();
    } else {
      return new Date(now.setMonth(now.getMonth() + 1)).toISOString();
    }
  };

  const updateLocalStorageWithSubscription = (
    subscription: any,
    isWhitelistPlan: boolean,
  ): void => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      let updatedUser;
      if (isWhitelistPlan) {
        updatedUser = {
          ...currentUser,
          subscribed: true,
          isWhitelisted: true,
          whitelistSubscription: subscription,
        };
      } else {
        updatedUser = {
          ...currentUser,
          subscribed: true,
          jobPostingSubscription: subscription,
        };
      }

      localStorage.setItem("user", JSON.stringify(updatedUser));
      console.log("💾 Local storage updated with subscription");
    } catch (error) {
      console.error("Failed to update local storage:", error);
    }
  };

  const dispatchSubscriptionUpdateEvent = (subscription: any): void => {
    try {
      const subscriptionEvent = new CustomEvent("subscriptionUpdated", {
        detail: {
          subscription,
          timestamp: new Date().toISOString(),
          plan: state.selectedPlan,
          billingPeriod: state.billingPeriod,
        },
      });
      window.dispatchEvent(subscriptionEvent);
      console.log("📢 Subscription update event dispatched");
    } catch (error) {
      console.error("Failed to dispatch subscription event:", error);
    }
  };

  const refreshUserData = async (): Promise<void> => {
    try {
      console.log("🔄 Attempting to refresh user data...");

      const response = await fetchWithRetry(`${API_BASE_URL}/auth/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const userData = await response.json();
        console.log("✅ User data refreshed successfully:", userData);

        // Update both context and localStorage
        updateUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));

        console.log("🔄 User context and localStorage updated");
      } else {
        console.warn(
          "⚠️ Failed to refresh user data, status:",
          response.status,
        );
        // Don't throw error, just use existing data
        const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (existingUser.id) {
          console.log("ℹ️ Using existing user data from localStorage");
          updateUser(existingUser);
        }
      }
    } catch (error) {
      console.error("❌ Failed to refresh user data:", error);
      // Don't break the payment flow if user refresh fails
      // Use existing data as fallback
      try {
        const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (existingUser.id) {
          console.log(
            "ℹ️ Fallback: Using existing user data from localStorage",
          );
          updateUser(existingUser);
        }
      } catch (parseError) {
        console.error("❌ Failed to parse existing user data:", parseError);
      }
    }
  };

  return { processPayment };
};

// Payment Method Selector Component
const PaymentMethodSelector: React.FC = () => {
  const { state, setPaymentMethod } = usePaymentContext();
  const { isDark } = useDarkMode();

  const paymentMethods = [
    { id: "mpesa" as PaymentMethod, name: "M-Pesa", icon: "📱" },
    { id: "card" as PaymentMethod, name: "Credit/Debit Card", icon: "💳" },
    { id: "paypal" as PaymentMethod, name: "PayPal", icon: "🔵" },
  ];

  return (
    <div className="mb-4 sm:mb-6">
      <label
        className={`block text-sm font-medium mb-2 sm:mb-3 ${isDark ? "text-gray-300" : "text-gray-700"}`}
      >
        Select Payment Method
      </label>
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        {paymentMethods.map((method) => (
          <button
            key={method.id}
            type="button"
            onClick={() => setPaymentMethod(method.id)}
            className={`p-3 sm:p-4 border-2 rounded-lg transition-all duration-200 flex items-center gap-2 sm:gap-3 text-left ${
              state.paymentMethod === method.id
                ? isDark
                  ? "border-blue-400 bg-blue-900/20 shadow-sm"
                  : "border-blue-500 bg-blue-50 shadow-sm"
                : isDark
                  ? "border-gray-600 hover:border-gray-500 hover:bg-gray-700/50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <span className="text-xl sm:text-2xl flex-shrink-0">
              {method.icon}
            </span>
            <span
              className={`font-medium text-sm sm:text-base ${isDark ? "text-gray-200" : "text-gray-800"}`}
            >
              {method.name}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// M-Pesa Phone Input Component - COMPLETELY FIXED VERSION
const MpesaPhoneInput: React.FC = () => {
  const { state, setMpesaPhone } = usePaymentContext();
  const { isDark } = useDarkMode();
  const [phoneError, setPhoneError] = useState<string>("");

  // ADD THIS DEBUG EFFECT
  useEffect(() => {
    console.log("🔍 MpesaPhoneInput State Updated:", {
      mpesaPhoneNumber: state.mpesaPhoneNumber,
      length: state.mpesaPhoneNumber?.length || 0,
    });
  }, [state.mpesaPhoneNumber]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, "");

    console.log("📱 Phone input change:", {
      display: value,
      cleaned: cleanedValue,
      previousState: state.mpesaPhoneNumber,
    });

    // Update context directly
    setMpesaPhone(cleanedValue);

    // Real-time validation
    if (cleanedValue) {
      if (!mpesaService.validatePhoneNumber(cleanedValue)) {
        setPhoneError(
          "Please enter a valid Kenyan phone number (07XXXXXXXX or 2547XXXXXXXX)",
        );
      } else {
        setPhoneError("");
      }
    } else {
      setPhoneError("");
    }
  };

  return (
    <div className="mb-4">
      <label
        className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
      >
        M-Pesa Phone Number
      </label>
      <input
        type="tel"
        placeholder="07XX XXX XXX or 2547XXXXXXXX"
        value={state.mpesaPhoneNumber || ""}
        onChange={handlePhoneChange}
        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base ${
          isDark
            ? "border-gray-600 bg-gray-700 text-white placeholder-gray-400"
            : "border-gray-300 bg-white text-gray-900"
        } ${phoneError ? "border-red-500" : ""}`}
        inputMode="numeric"
        pattern="[0-9\s]*"
      />
      {phoneError && <p className="mt-1 text-xs text-red-500">{phoneError}</p>}

      {/* Debug display */}
      <div className="mt-1 text-xs text-gray-500">
        Current value: "{state.mpesaPhoneNumber}" (Length:{" "}
        {state.mpesaPhoneNumber?.length || 0})
      </div>
    </div>
  );
};

// Checkout Form Component - FIXED VERSION
interface CheckoutFormProps {
  onSuccess: (paymentIntent: any) => void;
  onCancel: () => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { state, setError } = usePaymentContext();
  const { user } = useAuth();
  const { isDark } = useDarkMode();
  const [cardComplete, setCardComplete] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Use a stable ref for card element
  const cardElementRef = useRef<any>(null);
  const [isCardElementReady, setIsCardElementReady] = useState(false);

  const { processPayment } = usePaymentProcessor();

  // Initialize card element once when Stripe is ready
  // Effect 1: Initialize Stripe card element when conditions are met
  useEffect(() => {
    if (
      stripe &&
      elements &&
      state.paymentMethod === "card" &&
      !cardElementRef.current
    ) {
      try {
        const cardElement = elements.getElement(CardElement);
        if (cardElement) {
          cardElementRef.current = cardElement;
          setIsCardElementReady(true);
          console.log("✅ Card element initialized and stored in ref");
        } else {
          console.warn("⚠️ Card element not found in elements");
        }
      } catch (error) {
        console.error("❌ Failed to initialize card element:", error);
        setError("Failed to initialize payment form. Please refresh the page.");
      }
    }
  }, [stripe, elements, state.paymentMethod, setError]);

  // Effect 2: Debug logging for state changes
  useEffect(() => {
    console.log("🔍 CheckoutForm Full State:", {
      mpesaPhoneNumber: state.mpesaPhoneNumber,
      paymentMethod: state.paymentMethod,
      selectedPlan: state.selectedPlan?.name,
      billingPeriod: state.billingPeriod,
      isLoading: state.isLoading,
      error: state.error,
    });
  }, [state]);

  // You could add more focused effects for specific scenarios:

  // Effect 3: Reset card element when payment method changes away from card
  useEffect(() => {
    if (state.paymentMethod !== "card" && cardElementRef.current) {
      console.log("🔄 Payment method changed, clearing card element ref");
      cardElementRef.current = null;
      setIsCardElementReady(false);
    }
  }, [state.paymentMethod]);

  // Effect 4: Validate M-Pesa phone number when relevant
  useEffect(() => {
    if (state.paymentMethod === "mpesa" && state.mpesaPhoneNumber) {
      const isValid = mpesaService.validatePhoneNumber(state.mpesaPhoneNumber);
      console.log("📱 M-Pesa phone validation:", {
        phone: state.mpesaPhoneNumber,
        isValid,
        formatted: isValid
          ? mpesaService.formatPhoneNumber(state.mpesaPhoneNumber)
          : "invalid",
      });

      if (!isValid && state.mpesaPhoneNumber.length > 0) {
        console.warn("⚠️ Invalid M-Pesa phone number format");
      }
    }
  }, [state.paymentMethod, state.mpesaPhoneNumber]);

  // Effect 5: Handle payment success
  useEffect(() => {
    if (state.paymentIntent?.status === "succeeded") {
      console.log("🎉 Payment succeeded, triggering success flow");
      // You could trigger analytics, redirects, or other success actions here
    }
  }, [state.paymentIntent]);

  // Effect 6: Cleanup on component unmount
  useEffect(() => {
    return () => {
      console.log("🧹 CheckoutForm unmounting, cleaning up...");
      // Cleanup any resources, timeouts, or subscriptions
      cardElementRef.current = null;
    };
  }, []);
  const handleCardChange = useCallback(
    (event: any) => {
      setError(event.error?.message || "");
      setCardComplete(event.complete);
    },
    [setError],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!state.selectedPlan) {
        setError("No plan selected");
        return;
      }

      // Validate payment method specific requirements
      if (state.paymentMethod === "mpesa") {
        if (!state.mpesaPhoneNumber || state.mpesaPhoneNumber.trim() === "") {
          setError("Please enter your M-Pesa phone number");
          return;
        }
        if (!mpesaService.validatePhoneNumber(state.mpesaPhoneNumber)) {
          setError(
            "Please enter a valid Kenyan phone number (07XXXXXXXX or 2547XXXXXXXX)",
          );
          return;
        }
      }

      if (state.paymentMethod === "card") {
        if (!stripe || !elements) {
          setError("Payment system is still loading. Please wait a moment.");
          return;
        }

        // Use the stable ref instead of querying DOM
        if (!cardElementRef.current) {
          setError("Card input is not available. Please refresh the page.");
          return;
        }

        // Additional validation for card completion
        if (!cardComplete) {
          setError("Please complete your card details");
          return;
        }
      }

      setIsProcessing(true);
      setError("");

      try {
        await processPayment(
          stripe,
          cardElementRef.current,
          (paymentIntent) => {
            onSuccess(paymentIntent);
          },
        );
      } catch (error) {
        console.error("Payment processing error:", error);
      } finally {
        setIsProcessing(false);
      }
    },
    [
      state.selectedPlan,
      state.paymentMethod,
      state.mpesaPhoneNumber,
      stripe,
      elements,
      cardComplete,
      processPayment,
      setError,
      onSuccess,
    ],
  );

  if (!state.selectedPlan) {
    return (
      <div
        className={`w-full max-w-md mx-auto p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg border ${
          isDark ? "bg-gray-800 border-red-700/50" : "bg-white border-red-200"
        }`}
      >
        <div
          className={`text-center ${isDark ? "text-red-400" : "text-red-600"}`}
        >
          <p className="text-sm sm:text-base">
            No plan selected. Please go back and select a plan.
          </p>
          <button
            onClick={onCancel}
            className="mt-3 sm:mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm sm:text-base"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const amount =
    state.billingPeriod === "monthly"
      ? state.selectedPlan.monthlyPrice
      : state.selectedPlan.annualPrice;
  const periodLabel = state.billingPeriod === "monthly" ? "month" : "year";

  const isWhitelistPlan = state.selectedPlan?.name
    ?.toLowerCase()
    .includes("whitelist");
  const subscriptionType = isWhitelistPlan ? "Whitelist" : "Job Posting";

  return (
    <form
      onSubmit={handleSubmit}
      className={`w-full max-w-md mx-auto p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      {/* Header */}
      <div className="text-center mb-4 sm:mb-6">
        <h3
          className={`text-xl sm:text-2xl font-bold mb-1 ${isDark ? "text-white" : "text-gray-800"}`}
        >
          Complete Payment
        </h3>
        <p
          className={`text-xs sm:text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
        >
          Secure and encrypted payment processing
        </p>
      </div>

      {/* Plan Summary */}
      <div
        className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg sm:rounded-xl border ${
          isDark
            ? "bg-gray-700/50 border-gray-600"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <p
              className={`font-semibold text-sm sm:text-base truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}
            >
              {state.selectedPlan.name} Plan
            </p>
            <p className="text-lg sm:text-xl font-bold text-blue-500">
              KES {amount}
            </p>
            <p
              className={`text-xs sm:text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              per {periodLabel} • {subscriptionType}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 ml-2">
            <span
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-full whitespace-nowrap ${
                isDark
                  ? "bg-blue-900/50 text-blue-300"
                  : "bg-blue-100 text-blue-600"
              }`}
            >
              {state.billingPeriod}
            </span>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                isWhitelistPlan
                  ? isDark
                    ? "bg-green-900/50 text-green-300"
                    : "bg-green-100 text-green-600"
                  : isDark
                    ? "bg-purple-900/50 text-purple-300"
                    : "bg-purple-100 text-purple-600"
              }`}
            >
              {subscriptionType}
            </span>
          </div>
        </div>
        {user && (
          <div
            className={`mt-2 text-xs sm:text-sm truncate ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            Paying as:{" "}
            <strong className={isDark ? "text-gray-200" : "text-gray-800"}>
              {user.name}
            </strong>
          </div>
        )}
      </div>

      {/* Payment Method Selector */}
      <PaymentMethodSelector />

      {/* M-Pesa Phone Input */}
      {state.paymentMethod === "mpesa" && <MpesaPhoneInput />}

      {/* Card Details */}
      {state.paymentMethod === "card" && (
        <div className="mb-4">
          <label
            className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
          >
            Card Details
          </label>
          <div
            className={`border rounded-lg p-3 ${isDark ? "border-gray-600 bg-gray-700" : "border-gray-300 bg-white"}`}
          >
            {stripe && elements ? (
              <CardElement
                key="card-element"
                onChange={handleCardChange}
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: isDark ? "#ffffff" : "#424770",
                      "::placeholder": {
                        color: isDark ? "#9ca3af" : "#aab7c4",
                      },
                      fontFamily: "system-ui, -apple-system, sans-serif",
                      fontWeight: "400",
                    },
                  },
                  hidePostalCode: true,
                  disableLink: false,
                }}
                onReady={(element) => {
                  console.log("CardElement ready");
                  cardElementRef.current = element;
                  setIsCardElementReady(true);
                }}
              />
            ) : (
              <div
                className={`text-center py-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Loading payment form...
              </div>
            )}
          </div>
          {!isCardElementReady && state.paymentMethod === "card" && (
            <p
              className={`mt-2 text-xs ${isDark ? "text-yellow-400" : "text-yellow-600"}`}
            >
              Payment system is initializing. Please wait...
            </p>
          )}
        </div>
      )}

      {/* PayPal Info */}
      {state.paymentMethod === "paypal" && (
        <div
          className={`mb-4 p-3 sm:p-4 rounded-lg border ${
            isDark
              ? "bg-yellow-900/20 border-yellow-700/50"
              : "bg-yellow-50 border-yellow-200"
          }`}
        >
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <span className="text-xl sm:text-2xl">🔵</span>
            <span
              className={`font-semibold text-sm sm:text-base ${isDark ? "text-gray-200" : "text-gray-800"}`}
            >
              PayPal
            </span>
          </div>
          <p
            className={`text-xs sm:text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            You will be redirected to PayPal to complete your payment securely.
          </p>
        </div>
      )}

      {/* Error Display */}
      {state.error && (
        <div
          className={`mb-4 p-3 rounded-lg text-xs sm:text-sm border ${
            isDark
              ? "bg-red-900/20 text-red-300 border-red-700/50"
              : "bg-red-100 text-red-700 border-red-200"
          }`}
        >
          {state.error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className={`flex-1 py-3 px-4 border rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors font-medium text-sm sm:text-base order-2 sm:order-1 ${
            isDark
              ? "border-gray-600 text-gray-300 hover:bg-gray-700/50"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            isProcessing ||
            (state.paymentMethod === "card" &&
              (!isCardElementReady || !cardComplete)) ||
            (state.paymentMethod === "mpesa" && !state.mpesaPhoneNumber)
          }
          className="flex-1 py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm sm:text-base order-1 sm:order-2"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span className="hidden sm:inline">Processing...</span>
              <span className="sm:hidden">Processing</span>
            </span>
          ) : (
            `Pay KES ${amount}`
          )}
        </button>
      </div>

      {/* Security Notice */}
      <div className="mt-3 sm:mt-4 text-center">
        <p
          className={`text-xs flex items-center justify-center gap-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          <span>🔒</span>
          Payments are secure and encrypted
        </p>
      </div>
    </form>
  );
};

// Main Payment Form Component
interface PaymentFormProps {
  onSuccess: (paymentIntent: any) => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ onSuccess, onCancel }) => {
  const { isDark } = useDarkMode();
  const [stripeLoaded, setStripeLoaded] = useState<boolean>(false);
  const [stripeLoadAttempts, setStripeLoadAttempts] = useState(0);

  // Enhanced Stripe loading with retry mechanism
  useEffect(() => {
    const loadStripeWithRetry = async () => {
      try {
        const stripeInstance = await stripePromise;
        if (stripeInstance) {
          setStripeLoaded(true);
          console.log("✅ Stripe loaded successfully");
        } else {
          throw new Error("Stripe instance is null");
        }
      } catch (error) {
        console.error("Stripe loading failed:", error);
        if (stripeLoadAttempts < 3) {
          console.log(
            `Retrying Stripe load... attempt ${stripeLoadAttempts + 1}`,
          );
          setTimeout(() => {
            setStripeLoadAttempts((prev) => prev + 1);
          }, 2000);
        } else {
          console.error("Failed to load Stripe after 3 attempts");
        }
      }
    };

    loadStripeWithRetry();
  }, [stripeLoadAttempts]);

  // Memoize elements options to prevent re-renders
  const elementsOptions = useMemo(
    () => ({
      appearance: {
        theme: (isDark ? "night" : "stripe") as "night" | "stripe",
        variables: {
          colorPrimary: "#0570de",
          colorBackground: isDark ? "#1f2937" : "#ffffff",
          colorText: isDark ? "#f9fafb" : "#1f2937",
          colorDanger: "#df1b41",
          fontFamily: "system-ui, -apple-system, sans-serif",
          spacingUnit: "4px",
          borderRadius: "8px",
        },
        rules: {
          ".Input": {
            border: `1px solid ${isDark ? "#4b5563" : "#d1d5db"}`,
            backgroundColor: isDark ? "#374151" : "#ffffff",
            fontSize: "16px",
          },
          ".Input:focus": {
            borderColor: "#0570de",
            boxShadow: "0 0 0 1px #0570de",
          },
        },
      },
      loader: "always" as const,
    }),
    [isDark],
  );

  if (!stripeLoaded) {
    return (
      <div
        className={`w-full px-3 sm:px-4 py-4 sm:py-6 ${isDark ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div
          className={`w-full max-w-md mx-auto p-6 rounded-xl shadow-lg border ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
        >
          <LoadingSpinner
            isDark={isDark}
            message={
              stripeLoadAttempts > 0
                ? `Loading payment system... (attempt ${stripeLoadAttempts + 1}/3)`
                : "Loading payment system..."
            }
          />
          {stripeLoadAttempts >= 2 && (
            <p
              className={`mt-4 text-center text-sm ${isDark ? "text-yellow-400" : "text-yellow-600"}`}
            >
              Taking longer than usual. Please check your connection.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full px-3 sm:px-4 py-4 sm:py-6 ${isDark ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <Elements stripe={stripePromise} options={elementsOptions}>
        <CheckoutForm onSuccess={onSuccess} onCancel={onCancel} />
      </Elements>
    </div>
  );
};

export default PaymentForm;
